import { useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useStores } from '../../../../../stores/useStores';
import { formatTime } from '../../../../../utils/time';
import { useWidgetAdaptiveText } from '../../../hooks';

import styles from './ChallengeTimer.module.scss';

interface ChallengeTimerProps {
  extended?: boolean;
}

const DEFAULT_TIMER = '00:00:00';

function ChallengeTimerComponent(props: ChallengeTimerProps) {
  const { extended } = props;
  const { challengesStore } = useStores();
  const { t } = useTranslation();
  const { activeMissionProgress } = challengesStore;
  const [countdown, setCountdown] = useState<string>(DEFAULT_TIMER);
  const hasRefetchedRef = useRef(false);
  const { textRef } = useWidgetAdaptiveText({ padding: 4 });

  useEffect(() => {
    const expiresAt = activeMissionProgress?.expiresAt;
    if (!expiresAt) {
      setCountdown(DEFAULT_TIMER);
      hasRefetchedRef.current = false;
      return;
    }

    const updateCountdown = () => {
      const secondsLeft = dayjs(expiresAt).diff(dayjs(), 'seconds');
      if (secondsLeft < 0) {
        setCountdown(DEFAULT_TIMER);
        if (!hasRefetchedRef.current) {
          hasRefetchedRef.current = true;
          void challengesStore.fetch();
        }
        return;
      }
      hasRefetchedRef.current = false;
      setCountdown(formatTime(secondsLeft, 's', t));
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [activeMissionProgress?.expiresAt, challengesStore, t]);

  return (
    <div className={extended ? styles.containerExtended : styles.container}>
      <span ref={textRef} className={extended ? styles.textExtended : styles.text}>
        {countdown}
      </span>
    </div>
  );
}

export const ChallengeTimer = observer(ChallengeTimerComponent);
